function ResultsStep({
  chmPlusUnitMonthlyCost,
  currencyFormat,
  inputLabels,
  calculatorSettings,
  calculatorValues,
  setCalculatorValues,
  userSelections,
  results_step_content,
  debouncedCalculatorValues,
}) {
  // Recommended and Alternative Programs
  const [cardFlipped, setCardFlipped] = useState(false);
  // CHM Program Details
  const { cct_chm_programs } = cct_calculator_data;

  // CHM Program Units - at least 1 but no more than 3
  // prettier-ignore
  const chmProgramUnits = userSelections["people"] ?
    Number(userSelections["people"]) > 3 ? 3 :
      Number(userSelections["people"]) : 1;

  // Determine primary and alternative CHM program recommendations
  const recommendations = calculateRecommendedPrograms(userSelections);
  const recommendedProgram = recommendations[0];
  const alternativeProgram = recommendations[1] || null;

  // Calculate annual personal costs
  const personalCosts = calculatePersonalCosts(userSelections["solution"], debouncedCalculatorValues);
  // Calculate annual primary program costs
  const recommendedProgramCosts = calculateProgramCosts(
    cct_chm_programs[recommendedProgram],
    chmProgramUnits,
    chmPlusUnitMonthlyCost[recommendedProgram] || 0
  );
  // Calculate annual alternative program costs
  const alternativeProgramCosts =
    alternativeProgram &&
    calculateProgramCosts(
      cct_chm_programs[alternativeProgram],
      chmProgramUnits,
      chmPlusUnitMonthlyCost[alternativeProgram] || 0
    );
  // Calculate savings with primary program
  const recommendedSavings =
    personalCosts &&
    recommendedProgramCosts &&
    personalCosts["totalAnnualCosts"] - recommendedProgramCosts["totalAnnualCosts"];
  const recommendedProgramAnnualSavings = recommendedSavings > 0 ? recommendedSavings : 0;
  const recommendedProgramMonthlySavings = recommendedProgramAnnualSavings / 12;
  // Calculate savings with alternative program
  const alternativeSavings =
    personalCosts &&
    alternativeProgramCosts &&
    personalCosts["totalAnnualCosts"] - alternativeProgramCosts["totalAnnualCosts"];
  const alternativeProgramAnnualSavings = alternativeSavings > 0 ? alternativeSavings : 0;
  const alternativeProgramMonthlySavings = alternativeProgramAnnualSavings / 12;

  // Convert all calculations to integers and populate results page
  const userCalculations = {
    recommended_program: recommendedProgram,
    alternative_program: alternativeProgram,
    monthly_personal_costs: personalCosts && Number.parseFloat(personalCosts["totalMonthlyCosts"]).toFixed(0),
    annual_personal_costs: personalCosts && Number.parseFloat(personalCosts["totalAnnualCosts"]).toFixed(0),
    recommended_program_personal_responsibility:
      recommendedProgramCosts && Number.parseFloat(recommendedProgramCosts["programPersonalResponsibility"]),
    monthly_recommended_program_costs:
      recommendedProgramCosts && Number.parseFloat(recommendedProgramCosts["totalMonthlyCosts"]).toFixed(0),
    monthly_recommended_program_contribution:
      recommendedProgramCosts && Number.parseFloat(recommendedProgramCosts["totalMonthlyContribution"]).toFixed(0),
    annual_recommended_program_costs:
      recommendedProgramCosts && Number.parseFloat(recommendedProgramCosts["totalAnnualCosts"]).toFixed(0),
    alternative_program_personal_responsibility:
      alternativeProgramCosts && Number.parseFloat(alternativeProgramCosts["programPersonalResponsibility"]),
    monthly_alternative_program_costs:
      alternativeProgramCosts && Number.parseFloat(alternativeProgramCosts["totalMonthlyCosts"]).toFixed(0),
    monthly_alternative_program_contribution:
      alternativeProgramCosts && Number.parseFloat(alternativeProgramCosts["totalMonthlyContribution"]).toFixed(0),
    annual_alternative_program_costs:
      alternativeProgramCosts && Number.parseFloat(alternativeProgramCosts["totalAnnualCosts"]).toFixed(0),
    recommended_program_total_annual_savings: Number.parseFloat(recommendedProgramAnnualSavings).toFixed(0),
    recommended_program_total_monthly_savings: Number.parseFloat(recommendedProgramMonthlySavings).toFixed(0),
    alternative_program_total_annual_savings: Number.parseFloat(alternativeProgramAnnualSavings).toFixed(0),
    alternative_program_total_monthly_savings: Number.parseFloat(alternativeProgramMonthlySavings).toFixed(0),
  };

  // Do not show comparison results for Medicare
  const showResults = userSelections["solution"] !== "medicare";

  // Do not show calculator dropdown for No Insurance
  const showCalculatorDropdown = userSelections["solution"] !== "no_insurance";

  // Only show savings dropdown for Insurance
  const showSavingsDropdown = userSelections["solution"] === "insurance";

  // Dropdown calculator
  const [dropdownVisible, setDropdownVisible] = useState(null);
  const calculatorTriggerRef = useRef();
  const calculatorDropdownRef = useRef();
  const savingsTriggerRef = useRef();
  const savingsDropdownRef = useRef();

  // Display dropdown calculator
  function handleDropdownClick(e) {
    $(e.target).toggleClass("active");
    $(`#${e.target.dataset.dropdownTarget}`).slideToggle();
    setDropdownVisible(e.target.dataset.dropdownTarget);
  }

  // Listen for click events if dropdown is visible
  useEffect(() => {
    if (dropdownVisible) {
      document.addEventListener("mousedown", handleClickOutsideDropdown);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutsideDropdown);
    };
  }, [dropdownVisible]);

  // hide dropdown if click is outside dropdown and outside trigger
  const handleClickOutsideDropdown = (event) => {
    const currentDropdownVisible = dropdownVisible;
    if (currentDropdownVisible === "calculator-dropdown") {
      if (
        calculatorDropdownRef.current &&
        !calculatorDropdownRef.current.contains(event.target) &&
        calculatorTriggerRef.current &&
        !calculatorTriggerRef.current.contains(event.target)
      ) {
        $(`#${currentDropdownVisible}`).slideUp();
        setDropdownVisible(null);
      }
    }
    if (currentDropdownVisible === "savings-dropdown") {
      if (
        savingsDropdownRef.current &&
        !savingsDropdownRef.current.contains(event.target) &&
        savingsTriggerRef.current &&
        !savingsTriggerRef.current.contains(event.target)
      ) {
        $(`#${currentDropdownVisible}`).slideUp();
        setDropdownVisible(null);
      }
    }
  };

  // determine recommended program based on user selections
  function calculateRecommendedPrograms(userSelections) {
    if (
      userSelections["solution"] === "medicare" ||
      userSelections["age_over_65"] === "yes"
    ) {
      return ["seniorshare"];
    }
    // if (Number(userSelections["people"]) < 3 && userSelections["age_over_65"] === "yes") {
    //   return ["seniorshare"];
    // }
    if (userSelections["risk_tolerance"] === "low") {
      return ["bronze", "silver"];
    }
    if (userSelections["risk_tolerance"] === "high") {
      return ["gold", "silver"];
    }
    return ["silver", "gold"];
  }

  // populate results page description based on user flow selections
  const dynamicResultsDescription = () => {
    if (userSelections["solution"] === "medicare" && userSelections["age_over_65"] === "yes") {
      return results_step_content["user_flows"]["medicare_over_65"]["description"];
    }
    if (userSelections["solution"] === "medicare" && userSelections["age_over_65"] !== "yes") {
      return results_step_content["user_flows"]["medicare_under_65"]["description"];
    }
    if (userCalculations["recommended_program_total_annual_savings"] <= 0) {
      // no calculated savings
      return results_step_content["description_no_savings"].replace(
        "{chm_program_name}",
        cct_chm_programs[recommendedProgram].name
      );
    }
    if (userSelections["solution"] === "insurance") {
      return results_step_content["user_flows"]["insurance"]["description"];
    }
    if (userSelections["solution"] === "no_insurance") {
      return results_step_content["user_flows"]["no_insurance"]["description"];
    }
    if (userSelections["solution"] === "other_cost_sharing_service") {
      return results_step_content["user_flows"]["other_cost_sharing_service"]["description"];
    }
    return results_step_content["description"];
  };

  // Personal Cost Calculations
  function calculatePersonalCosts(solution, calculatorValues) {
    if (solution === "insurance") {
      const annualPremiumCosts = Number(calculatorValues["monthly_premium"]) * 12;
      const annualOOPSpending = Number(calculatorValues["yearly_out_of_pocket_medical_spending"]);
      const annualDrOfficeVisitCost = Number(calculatorValues["yearly_doctors_office_cost"]);
      const deductible = Number(calculatorValues["deductible"]);
      // const totalCosts = annualPremiumCosts + annualDrOfficeVisitCost + deductible;
      const totalCosts = annualPremiumCosts + annualOOPSpending + deductible;
      return {
        totalAnnualCosts: totalCosts,
        totalMonthlyCosts: totalCosts / 12,
      };
    } else if (solution === "other_cost_sharing_service") {
      const annualContributionCosts = Number(calculatorValues["monthly_contribution"]) * 12;
      const annualOOPSpending = Number(calculatorValues["yearly_out_of_pocket_medical_spending"]);
      const totalCosts = annualContributionCosts + annualOOPSpending;
      return {
        totalAnnualCosts: totalCosts,
        totalMonthlyCosts: totalCosts / 12,
      };
    } else if (solution === "no_insurance") {
      const annualOOPSpending = Number(calculatorValues["yearly_out_of_pocket_medical_spending"]);
      const totalCosts = annualOOPSpending;
      return {
        totalAnnualCosts: totalCosts,
        totalMonthlyCosts: totalCosts / 12,
      };
    } else
      return {
        totalAnnualCosts: 0,
        totalMonthlyCosts: 0,
      };
  }

  // CHM Program Cost Calculations
  function calculateProgramCosts(program, units, chmPlusUnitMonthlyCost) {
    const programAnnualPremiumCosts = units * Number(program["monthly_cost_per_unit"]) * 12;
    const chmPlusAnnualCost = units * Number(chmPlusUnitMonthlyCost) * 12;
    const programPersonalResponsibility = units * Number(program["personal_responsibility_per_unit"]);
    // const programTotalAnnualCosts = programAnnualPremiumCosts + chmPlusAnnualCost + programPersonalResponsibility;
    const programTotalAnnualCosts = units * parseFloat(program["annual_program_cost_per_unit"].replace(/[^0-9.]/g, ''));

    const totalMonthlyContribution = (programAnnualPremiumCosts + chmPlusAnnualCost) / 12;

    return {
      programPersonalResponsibility: programPersonalResponsibility,
      totalAnnualCosts: programTotalAnnualCosts,
      totalMonthlyCosts: programTotalAnnualCosts / 12,
      totalMonthlyContribution: totalMonthlyContribution,
    };
  }

  // Flip between recommended programs
  function handleFlipCards() {
    setCardFlipped(!cardFlipped);
    const cardFlipper = $(".card-flipper");
    const cardWrapper = $(".card-wrapper");
    const firstCard = $(".card-wrapper .card:first-child");
    const lastCard = $(".card-wrapper .card:last-child");
    if ($(cardFlipper).hasClass("flipped")) {
      // flip to first card
      $(cardFlipper).toggleClass("unflipped");
      $(cardFlipper).removeClass("flipped");
      $(cardWrapper).css("min-height", $(firstCard).outerHeight(true));
    } else {
      // flip to second card
      $(cardFlipper).addClass("flipped");
      $(cardFlipper).removeClass("unflipped");
      $(cardWrapper).css("min-height", $(lastCard).outerHeight(true));
    }
  }

  // track tool completion event
  useEffect(() => {
    dataLayer.push({
      tool_name: "cost_comparison",
      event: "tool_complete",
      engagement_type: "view_results",
      step_name: "results",
      ...userCalculations,
    });
  }, []);

  // TODO: only track event for successful submission
  // track form submission event
  $(document).on("click", ".hbspt-form form .hs-submit input[type=submit]", function (e) {
    dataLayer.push({
      tool_name: "cost_comparison",
      event: "form_submission",
      engagement_type: "form_submission",
      step_name: "results",
      ...userCalculations,
    });
  });

  return (
    <>
      <div id="results" className={`cost-comparison-tool__card`}>
        <div className="cost-comparison-tool__container">
          <h2 className="cost-comparison-tool__card-title">{results_step_content["title"]}</h2>
          <p className="cost-comparison-tool__card-description">
            {dynamicResultsDescription().replace("{chm_program_name}", cct_chm_programs[recommendedProgram].name)}
          </p>
        </div>
        {showResults && (
          <div className="results-grid">
            <div className="results-block">
              <div className="results-block__content-wrapper">
                <div className="results-block__content">
                  <p className="results-block__title">
                    Estimated personal cost for 1 year<sup>*</sup>
                  </p>
                  <p className="results-block__number">
                    {Intl.NumberFormat("en-US", currencyFormat).format(userCalculations["annual_personal_costs"])}
                  </p>
                  {showCalculatorDropdown && (
                    <a
                      id="results-block-dropdown-trigger"
                      className={`btn-link results-block-dropdown__trigger ${dropdownVisible === "calculator-dropdown" ? "active" : ""
                        }`}
                      onClick={handleDropdownClick}
                      ref={calculatorTriggerRef}
                      data-dropdown-target="calculator-dropdown"
                    >
                      Your submitted values{" "}
                      <span className="results-block-dropdown__trigger__icon">
                        <i className="fa-regular fa-chevron-down"></i>
                      </span>
                    </a>
                  )}
                </div>
              </div>
              {showCalculatorDropdown && (
                <div id="calculator-dropdown" className={`results-block-dropdown`} ref={calculatorDropdownRef}>
                  <Calculator
                    inputLabels={inputLabels}
                    calculatorSettings={calculatorSettings}
                    calculatorValues={calculatorValues}
                    setCalculatorValues={setCalculatorValues}
                  />
                </div>
              )}
            </div>
            <div className="results-block results-block--secondary">
              <div className="results-block__content-wrapper">
                <div className="results-block__content">
                  <p className="results-block__title">
                    Your CHM <strong>Savings</strong>
                  </p>
                  <p className="results-block__number">
                    {Intl.NumberFormat("en-US", currencyFormat).format(
                      !cardFlipped
                        ? userCalculations["recommended_program_total_annual_savings"]
                        : userCalculations["alternative_program_total_annual_savings"]
                    )}
                  </p>
                  {showSavingsDropdown && (
                    <a
                      id="savings-dropdown-trigger"
                      className={`btn-link results-block-dropdown__trigger ${dropdownVisible === "savings-dropdown" ? "active" : ""
                        }`}
                      onClick={handleDropdownClick}
                      ref={savingsTriggerRef}
                      data-dropdown-target="savings-dropdown"
                    >
                      Compare savings and benefits{" "}
                      <span className="results-block-dropdown__trigger__icon">
                        <i className="fa-regular fa-chevron-down"></i>
                      </span>
                    </a>
                  )}
                </div>
              </div>
            </div>
            <div className="results-block">
              <div className="results-block__content-wrapper">
                <div className="results-block__content">
                  <p className="results-block__title">Estimated CHM cost for 1 year</p>
                  <p className="results-block__number">
                    {Intl.NumberFormat("en-US", currencyFormat).format(
                      !cardFlipped
                        ? userCalculations["annual_recommended_program_costs"]
                        : userCalculations["annual_alternative_program_costs"]
                    )}
                  </p>
                </div>
              </div>
            </div>
            {showSavingsDropdown && (
              <div
                id="savings-dropdown"
                className="results-block-dropdown results-block-dropdown--wide"
                ref={savingsDropdownRef}
              >
                <div class="table table--cct">
                  <div class="table-head">
                    <div class="table-row">
                      <div class="table-cell">Feature</div>
                      <div class="table-cell">Insurance</div>
                      <div class="table-cell">CHM</div>
                      <div class="table-cell">Feature</div>
                    </div>
                    {/* <!-- end .table-row --> */}
                  </div>
                  {/* <!-- end .table-head -->  */}
                  <div class="table-body">
                    <div class="table-row">
                      <div class="table-cell">Monthly Premium</div>
                      <div class="table-cell">
                        {Intl.NumberFormat("en-US", currencyFormat).format(
                          debouncedCalculatorValues["monthly_premium"]
                        )}
                      </div>
                      <div class="table-cell">
                        {Intl.NumberFormat("en-US", currencyFormat).format(
                          !cardFlipped
                            ? userCalculations["monthly_recommended_program_contribution"]
                            : userCalculations["monthly_alternative_program_contribution"]
                        )}
                      </div>
                      <div class="table-cell">Monthly Contribution</div>
                    </div>
                    {/* <!-- end .table-row --> */}
                    <div class="table-row">
                      <div class="table-cell">Network requirement</div>
                      <div class="table-cell">yes</div>
                      <div class="table-cell">no</div>
                      <div class="table-cell">Network requirement</div>
                    </div>
                    {/* <!-- end .table-row --> */}
                    <div class="table-row">
                      <div class="table-cell">Deductible</div>
                      <div class="table-cell">
                        {Intl.NumberFormat("en-US", currencyFormat).format(debouncedCalculatorValues["deductible"])}
                      </div>
                      <div class="table-cell">
                        {Intl.NumberFormat("en-US", currencyFormat).format(
                          !cardFlipped
                            ? userCalculations["recommended_program_personal_responsibility"]
                            : userCalculations["alternative_program_personal_responsibility"]
                        )}
                      </div>
                      <div class="table-cell">Personal Responsibility</div>
                    </div>
                    {/* <!-- end .table-row --> */}
                    <div class="table-row">
                      <div class="table-cell">Co-pay after Deductible</div>
                      <div class="table-cell">
                        {Intl.NumberFormat("en-US", currencyFormat).format(
                          debouncedCalculatorValues["copay_after_deductible"]
                        )}
                      </div>
                      <div class="table-cell">N/A</div>
                      <div class="table-cell">Co-pay after Personal Responsibility</div>
                    </div>
                    {/* <!-- end .table-row --> */}
                    <div class="table-row">
                      <div class="table-cell">Yearly Cost office visit</div>
                      <div class="table-cell">
                        {Intl.NumberFormat("en-US", currencyFormat).format(
                          debouncedCalculatorValues["yearly_doctors_office_cost"]
                        )}
                      </div>
                      <div class="table-cell">100%</div>
                      <div class="table-cell">Cost per office visit (each)</div>
                    </div>
                    {/* <!-- end .table-row --> */}
                    <div class="table-row">
                      <div class="table-cell">Preauthorization required</div>
                      <div class="table-cell">Yes</div>
                      <div class="table-cell">N/A</div>
                      <div class="table-cell">Preauthorization required</div>
                    </div>
                    {/* <!-- end .table-row --> */}
                    <div class="table-row">
                      <div class="table-cell">Telemedicine</div>
                      <div class="table-cell">Included</div>
                      <div class="table-cell">Included</div>
                      <div class="table-cell">Telemedicine</div>
                    </div>
                    {/* <!-- end .table-row --> */}
                    <div class="table-row">
                      <div class="table-cell">Yearly Maintenance Pharmaceuticals</div>
                      <div class="table-cell">
                        {Intl.NumberFormat("en-US", currencyFormat).format(
                          debouncedCalculatorValues["yearly_maintenance_pharmaceuticals"]
                        )}
                      </div>
                      <div class="table-cell">Not included</div>
                      <div class="table-cell">Maintenance Pharmaceuticals</div>
                    </div>
                    {/* <!-- end .table-row --> */}
                  </div>
                  {/* <!-- end .table-body --> 
                  <div class="table-footer">
                    <div class="table-row">
                      <div class="table-cell">Monthly Total</div>
                      <div class="table-cell">
                        {Intl.NumberFormat("en-US", currencyFormat).format(userCalculations["monthly_personal_costs"])}
                      </div>
                      <div class="table-cell">
                        {Intl.NumberFormat("en-US", currencyFormat).format(
                          !cardFlipped
                            ? userCalculations["monthly_recommended_program_costs"]
                            : userCalculations["monthly_alternative_program_costs"]
                        )}
                      </div>
                      <div class="table-cell">Monthly Total</div>
                    </div>
                    {/* <!-- end .table-row --> 
                    <div class="table-row">
                      <div class="table-cell">Yearly Total</div>
                      <div class="table-cell">
                        {Intl.NumberFormat("en-US", currencyFormat).format(userCalculations["annual_personal_costs"])}
                      </div>
                      <div class="table-cell">
                        {Intl.NumberFormat("en-US", currencyFormat).format(
                          !cardFlipped
                            ? userCalculations["annual_recommended_program_costs"]
                            : userCalculations["annual_alternative_program_costs"]
                        )}
                      </div>
                      <div class="table-cell">Yearly Total</div>
                    </div>
                    {/* <!-- end .table-row --> 
                    <div class="table-row">
                      <div class="table-cell"></div>
                      <div class="table-cell"></div>
                      <div class="table-cell">
                        {Intl.NumberFormat("en-US", currencyFormat).format(
                          !cardFlipped
                            ? userCalculations["recommended_program_total_monthly_savings"]
                            : userCalculations["alternative_program_total_monthly_savings"]
                        )}
                      </div>
                      <div class="table-cell">Your Monthly CHM Savings</div>
                    </div>
                    {/* <!-- end .table-row --> 
                    <div class="table-row">
                      <div class="table-cell"></div>
                      <div class="table-cell"></div>
                      <div class="table-cell">
                        {Intl.NumberFormat("en-US", currencyFormat).format(
                          !cardFlipped
                            ? userCalculations["recommended_program_total_annual_savings"]
                            : userCalculations["alternative_program_total_annual_savings"]
                        )}
                      </div>
                      <div class="table-cell">Your Yearly CHM Savings</div>
                    </div>
                    {/* <!-- end .table-row --> 
                  </div>
                  {/* <!-- end .table-footer --> */}
                </div>
                {/* <!-- end .table -->  */}
              </div>
            )}
          </div>
        )}
        <div id="chm-recommendations" className="chm-recommendations">
          <div className={alternativeProgram ? `card-flipper` : `card-single`}>
            <div className="card-wrapper">
              <Recommendation
                currencyFormat={currencyFormat}
                programId={userCalculations["recommended_program"]}
                title="Recommended"
                monthlyProgramContribution={userCalculations["monthly_recommended_program_contribution"]}
                programPersonalResponsibility={userCalculations["recommended_program_personal_responsibility"]}
              />
              {alternativeProgram && (
                <Recommendation
                  currencyFormat={currencyFormat}
                  programId={userCalculations["alternative_program"]}
                  title="Alternative"
                  monthlyProgramContribution={userCalculations["monthly_alternative_program_contribution"]}
                  programPersonalResponsibility={userCalculations["alternative_program_personal_responsibility"]}
                />
              )}
            </div>
          </div>
          {alternativeProgram && (
            <div className="card-flipper__buttons">
              <button
                onClick={handleFlipCards}
                className={!cardFlipped && "disabled"}
                disabled={!cardFlipped && "disabled"}
              >
                <i class="fa-solid fa-angle-left"></i>
              </button>
              <button
                onClick={handleFlipCards}
                className={cardFlipped && "disabled"}
                disabled={cardFlipped && "disabled"}
              >
                <i class="fa-solid fa-angle-right"></i>
              </button>
            </div>
          )}
          <p className="cost-comparison-tool__scroll-prompt">
            <a
              href="#results-form"
              className="btn-link btn-link--text-with-icon"
              onClick={() => {
                dataLayer.push({
                  tool_name: "cost_comparison",
                  event: "tool_engagement",
                  engagement_type: "scroll_click",
                  step_name: "results",
                });
              }}
            >
              {results_step_content["scroll_prompt_button_text"]}{" "}
              <span className="btn-link__icon">
                <i className="fa-regular fa-arrow-down"></i>
              </span>
            </a>
          </p>
        </div>
      </div>
      <HubspotForm userSelections={userSelections} userCalculations={userCalculations} />
    </>
  );
}
